import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, API_BASE, safeBlobDownload } from "@/lib/api";
import { formatInr } from "@/lib/currency";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { DownloadSimple, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";

function tokenHeaders() {
  const t = localStorage.getItem("aig_access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

export default function ReconciliationPage() {
  const [parties, setParties] = useState([]);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    api.get("/reconciliation/parties").then((r) => setParties(r.data));
    api.get("/reconciliation/batches").then((r) => setBatches(r.data));
  }, []);

  return (
    <div className="p-8 space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Scales size={26} weight="duotone" /> Reconciliation
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Party- and batch-level reconciliation. Approval is blocked unless the difference is exactly ₹0.00.
        </p>
      </div>

      <Tabs defaultValue="parties">
        <TabsList>
          <TabsTrigger value="parties" data-testid="tab-parties">Parties</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches">Batches</TabsTrigger>
        </TabsList>

        <TabsContent value="parties">
          <div className="card-panel mt-4">
            <table className="w-full table-dense" data-testid="reconciliation-parties-table">
              <thead className="bg-muted/60">
                <tr>
                  <th className="text-left">Party</th>
                  <th className="text-left">Batch Row</th>
                  <th className="text-right">Imported ₹</th>
                  <th className="text-right">Invoices</th>
                  <th className="text-right">Generated ₹</th>
                  <th className="text-right">Diff ₹</th>
                  <th className="text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {parties.map((r) => (
                  <tr key={r.imported_row_id} className="border-t border-border/60 hover:bg-muted/40">
                    <td>
                      <Link to={`/parties/${r.party_id}`} className="hover:underline">{r.party_name}</Link>
                    </td>
                    <td className="text-xs">
                      <Link to={`/batches/${r.batch_id}`} className="hover:underline">#{r.row_number}</Link>
                    </td>
                    <td className="num-cell">{formatInr(r.imported_total)}</td>
                    <td className="num-cell">{r.invoice_count}</td>
                    <td className="num-cell">{formatInr(r.generated_total)}</td>
                    <td className={`num-cell ${Number(r.difference) === 0 ? "text-emerald-700" : "text-rose-700"}`}>
                      {formatInr(r.difference)}
                    </td>
                    <td><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
                {parties.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">Nothing to reconcile yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="batches">
          <div className="card-panel mt-4">
            <table className="w-full table-dense" data-testid="reconciliation-batches-table">
              <thead className="bg-muted/60">
                <tr>
                  <th className="text-left">Batch</th>
                  <th className="text-right">Imported ₹</th>
                  <th className="text-right">Generated ₹</th>
                  <th className="text-right">Diff ₹</th>
                  <th className="text-right">Draft</th>
                  <th className="text-right">Approved</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b.batch_id} className="border-t border-border/60 hover:bg-muted/40">
                    <td><Link to={`/batches/${b.batch_id}`} className="hover:underline">{b.filename}</Link></td>
                    <td className="num-cell">{formatInr(b.imported_total)}</td>
                    <td className="num-cell">{formatInr(b.generated_total)}</td>
                    <td className={`num-cell ${Number(b.difference) === 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatInr(b.difference)}</td>
                    <td className="num-cell">{b.draft_count}</td>
                    <td className="num-cell">{b.approved_count}</td>
                    <td className="text-right">
                      <Button size="sm" variant="ghost" onClick={async () => {
                        const res = await fetch(`${API_BASE}/imports/batches/${b.batch_id}/reconciliation.xlsx`, { credentials: "include", headers: tokenHeaders() });
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url; a.download = `reconciliation_${b.batch_id}.xlsx`; a.click();
                        URL.revokeObjectURL(url);
                      }} data-testid={`recon-batch-xlsx-${b.batch_id}`}>
                        <DownloadSimple size={14} className="mr-1" />.xlsx
                      </Button>
                    </td>
                  </tr>
                ))}
                {batches.length === 0 && (
                  <tr><td colSpan="7" className="text-center py-10 text-muted-foreground">No committed batches yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
